import React, { useState, useContext } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ListSubheader,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HubOutlined from '@mui/icons-material/HubOutlined';
import ExtensionIcon from '@mui/icons-material/Extension';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import BarChartIcon from '@mui/icons-material/BarChart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import GoogleSignInButton from './GoogleSignInButton';
import { OrganizationContext } from '../context/OrganizationContext';
import { useThemeManager } from '../context/ThemeContext';

const drawerWidth = 300;

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: `${theme.spacing(1)} !important`,
  minWidth: '120px !important',
  '& .MuiInputLabel-root': {
    color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
  },
  '& .MuiSelect-root': {
    color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'white' : 'inherit',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'white' : 'inherit',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'white' : 'inherit',
    },
    '.MuiSvgIcon-root': {
      color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
    },
  },
}));

interface LayoutProps {
  children: React.ReactNode;
  googleIdToken: string | null;
  onSignInSuccess: (token: string) => void;
  onSignOutSuccess: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, googleIdToken, onSignInSuccess, onSignOutSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const organizationContext = useContext(OrganizationContext);
  const { mode, toggleTheme } = useThemeManager();
  const theme = useTheme();

  if (!organizationContext) {
    return null; // Or a loading indicator
  }

  const { organizations, selectedOrganization } = organizationContext;

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleMenuItemClick = (path: string) => {
    const orgId = searchParams.get('orgId');
    const search = orgId ? `?orgId=${orgId}` : '';
    navigate({
      pathname: path,
      search,
    });
    handleDrawerClose();
  };

  const selectedStyle = {
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
    '&.Mui-selected:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  };

  const logoSrc = mode === 'dark' ? '/logo_w.png' : '/logo.png';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            variant="temporary"
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                borderTopRightRadius: 16,
                borderBottomRightRadius: 16,
              },
            }}
          >
            <Box
              sx={{ width: drawerWidth, display: 'flex', flexDirection: 'column', height: '100%' }}
              role="presentation"
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <img src={logoSrc} alt="Aida Logo" style={{ height: '30px' }} />
              </Box>
              <Divider />
              <List subheader={<ListSubheader>Main</ListSubheader>}>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname === '/'} onClick={() => handleMenuItemClick('/')}>
                    <ListItemIcon>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Home" />
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider />
              <List
                subheader={<ListSubheader>Manage Agents</ListSubheader>}>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/agents')} onClick={() => handleMenuItemClick('/agents')}>
                    <ListItemIcon>
                      <SmartToyIcon />
                    </ListItemIcon>
                    <ListItemText primary="Agents" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/tools')} onClick={() => handleMenuItemClick('/tools')}>
                    <ListItemIcon>
                      <ExtensionIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tools" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname === '/connections'} onClick={() => handleMenuItemClick('/connections')}>
                    <ListItemIcon>
                      <HubOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Connections" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/whatsapp')} onClick={() => handleMenuItemClick('/whatsapp')}>
                    <ListItemIcon>
                      <WhatsAppIcon />
                    </ListItemIcon>
                    <ListItemText primary="WhatsApp" />
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider />
              <List subheader={<ListSubheader>Manage Organization</ListSubheader>}>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/users')} onClick={() => handleMenuItemClick('/users')}>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/roles')} onClick={() => handleMenuItemClick('/roles')}>
                    <ListItemIcon>
                      <AdminPanelSettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Roles" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/dashboard')} onClick={() => handleMenuItemClick('/dashboard')}>
                    <ListItemIcon>
                      <BarChartIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname.startsWith('/quotas')} onClick={() => handleMenuItemClick('/quotas')}>
                    <ListItemIcon>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Quotas" />
                  </ListItemButton>
                </ListItem>
              </List>
              <Box sx={{ flexGrow: 1 }} />
              <Divider />
              <List>
                <ListItem disablePadding>
                  <ListItemButton sx={selectedStyle} selected={location.pathname === '/help'} onClick={() => handleMenuItemClick('/help')}>
                    <ListItemIcon>
                      <HelpOutlineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Help" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>
          <Box sx={{ flexGrow: 1, cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => {
            const orgId = searchParams.get('orgId');
            const search = orgId ? `?orgId=${orgId}` : '';
            navigate({ pathname: '/', search });
          }}>
            <img src={logoSrc} alt="Aida Logo" style={{ height: '20px', marginRight: '10px' }} />
            <Typography variant="h6" noWrap component="span" sx={{ color: 'inherit', fontWeight: 'bold', fontSize: '1.25rem', marginTop: '2px' }}>
              Agent Builder
            </Typography>
          </Box>
          <StyledFormControl size="small" variant="outlined">
            <InputLabel id="organization-select-label">Organization</InputLabel>
            <Select
              labelId="organization-select-label"
              id="organization-select"
              value={selectedOrganization ? selectedOrganization.id : ''}
              label="Organization"
              onChange={(e) => {
                const newOrgId = e.target.value;
                window.location.href = `/?orgId=${newOrgId}`;
              }}
            >
              {organizations.map(org => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </StyledFormControl>
          <IconButton sx={{ marginLeft: '8px !important' }} onClick={toggleTheme} color="inherit">
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          <GoogleSignInButton
            googleIdToken={googleIdToken}
            onSignInSuccess={onSignInSuccess}
            onSignOutSuccess={onSignOutSuccess}
          />
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        <Container sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;